<script setup>
import { ref, computed } from 'vue';
import { useForm, useField } from 'vee-validate';
import { translator } from '@shop/frontend/web/js/services/TranslatorService';
import IModal from '@components/IModal/IModal.vue';
import IInput from '@components/IInput/IInput.vue';
import useCartStore from '@vueApp/store/modules/cart.js';
import validationSchema from '@vueApp/composables/validationSchema.js';
import api from '@shop/frontend/web/js/api';

const cartStore = useCartStore();
const showSocial = ref(window.SHOW_SOCIAL_AUTH);

translator.getLanguages();

const agreeState = ref(false);

const actionLink = computed(() => {
  if (process.env.NODE_ENV === 'development') {
    return `/user/registration/fast-register?event_id=${cartStore.getcurrentOpenedEventId.value}&type=${cartStore.eventTypeToPay.value}`;
  }

  const lang = document.querySelector('html').getAttribute('lang') ?? 'ru';
  return `/${lang}/user/registration/fast-register?event_id=${cartStore.getcurrentOpenedEventId.value}&type=${cartStore.eventTypeToPay.value}`;
});

function closeFastBuyModal() {
  cartStore.setFastBuyModalStatus(false);
}

// TODO перенести получение csrfToken в stateManager
const csrfToken = document.head.querySelector('meta[name="csrf-token"]');

const {
  values,
  errors,
  setErrors,
  defineInputBinds,
  handleSubmit,
  setFieldValue,
} = useForm({
  validationSchema,
  initialValues: {
    agree: false,
    userAgreement: true,
    isSubscribed: false,
  }
});

const email = defineInputBinds('email');
const first = defineInputBinds('first');
const second = defineInputBinds('second');
const middle = defineInputBinds('middle');
const phone = defineInputBinds('phone');
const agree = defineInputBinds('agree');
const middleField = useField('middle');
const userAgreement = defineInputBinds("userAgreement");
const isSubscribed = defineInputBinds("isSubscribed");

const preparePhone = () => {
  if (!values.phone) {
    setFieldValue('phone', 'null');
  }
};

const isAccuiringButtonActive = computed(() => {
  const requiredFieldsFilled = 
    values.email && 
    values.first && 
    values.second && 
    values.phone && 
    agreeState.value;
  
  const noValidationErrors = 
    !errors.email && 
    !errors.first && 
    !errors.second && 
    !errors.phone && 
    !errors.agree;
  
  return requiredFieldsFilled && noValidationErrors;
})

const redirectUri = ref(`${window.location.origin}/social/fast-auth`);

const submitForm = handleSubmit ((values) => {
  const formObject = {
    email: email.value.value,
    first: first.value.value,
    second: second.value.value,
    middle: middle.value.value,
    phone: phone.value.value,
    agree: Number(values.agree),
    user_agreement: Number(values.userAgreement),
    is_subscribed: Number(values.isSubscribed),
  };
  const fieldsForm = validationSchema._nodes;

  api.user.fastRegisterAjax({eventId: cartStore.getcurrentOpenedEventId.value, type: cartStore.eventTypeToPay.value, FastRegistrationForm: formObject, csrf: csrfToken.content, ajax: 'fast-registration-controller' })
  .then((res) => {
    if (res.redirectUrl) {
      window.location.replace(res.redirectUrl)
    }
    
    fieldsForm.forEach((value) => {
      setErrors({
        [value]: res[value],
      })
    })
  })
})
</script>

<template>
  <i-modal id="fastRegisterModal" modal-class="user-modal fast-register-modal" @hide="closeFastBuyModal()">
    <template #header>
      <span class="modal-title-header">
        {{ translator.t("main", "Fast pay") }}
      </span>
      <!-- TODO: Убрать data-target и класс js-get-modal. Переделать на vue -->
      <button class="modal-title-btn js-get-modal" data-target="#loginModal" @click="closeFastBuyModal()">
        {{ translator.t("main", "Sign in") }}
      </button>
    </template>
    <template #body>
      <div v-if="showSocial" class="social_enter">
        <div id="infomatika_connect" style="display: flex;" data-infomatika-connect="providers=vk,google,yandex;redirect_uri=/ru/social-auth/handle;">
          <div data-provider="vk" class="ic-button-provider ic-button-provider__vk"></div>
          <div data-provider="google" class="ic-button-provider ic-button-provider__google"></div>
          <div data-provider="yandex" class="ic-button-provider ic-button-provider__yandex"></div>
        </div>
      </div>

      <div class="modal-body-info">
        {{
          translator.t(
            "main",
            "Fill in all fields to proceed to the purchase. After payment, tickets will be sent to the specified e-mail."
          )
        }}
      </div>

      <form :action="actionLink" method="post" autocomplete="off" @submit="submitForm">
        <fieldset>
          <input type="hidden" name="_csrf-frontend" :value="csrfToken.content">
          <div class="user-modal__input-wrapper">
            <i-input
              type="email"
              :placeholder="translator.t('user', 'E-mail')"
              input-id="fastregistrationform-email"
              :is-label="true"
              :is-required="true"
              :bind-name="email"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[email]"
            />
            <div v-if="errors.email" class="user-modal__input-error">
              {{ errors.email }}
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div class="user-modal__input-wrapper">
            <i-input
              type="text"
              :placeholder="translator.t('user', 'Name')"
              input-id="fastregistrationform-name"
              :is-label="true"
              :is-required="true"
              :bind-name="first"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[first]"
            />
            <div v-if="errors.first" class="user-modal__input-error">
              {{ errors.first }}
            </div>
          </div>

          <div class="user-modal__input-wrapper">
            <i-input
              type="text"
              :placeholder="translator.t('user', 'Surname')"
              input-id="fastregistrationform-surname"
              :is-label="true"
              :is-required="true"
              :bind-name="second"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[second]"
            />
            <div v-if="errors.second" class="user-modal__input-error">
              {{ errors.second }}
            </div>
          </div>

          <div class="user-modal__input-wrapper">
            <i-input
              type="text"
              :placeholder="translator.t('user', 'Patronymic')"
              input-id="fastregistrationform-middle"
              :is-label="true"
              :is-required="false"
              :bind-name="middle"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[middle]"
              @input="values.middle === '' ? middleField.handleReset() : null"
              @change="values.middle === '' ? middleField.handleReset() : null"
            />
            <div v-if="errors.middle" class="user-modal__input-error">
              {{ errors.middle }}
            </div>
          </div>

          <div class="user-modal__input-wrapper">
            <i-input
              type="tel"
              :placeholder="translator.t('user', 'Phone')"
              mask="+7(###)###-##-##"
              input-id="fastregistrationform-tel"
              :is-label="true"
              :is-required="true"
              :bind-name="phone"
              wrapper-class="user-modal__input"
              name="FastRegistrationForm[phone]"
              @focused="preparePhone"
            />
            <div v-if="errors.phone" class="user-modal__input-error">
              {{ errors.phone }}
            </div>
          </div>
        </fieldset>
        <div class="user-modal__input-wrapper user-modal__input-wrapper_user-agreement">
          <label class="user-modal__agree-label">
            <input
              id="fastregistrationform-user_agreement"
              v-bind="userAgreement"
              :checked="values.userAgreement"
              @change="setFieldValue(
                'userAgreement',
                $event.target.checked
              )"
              class="custom-checkbox-input"
              name="FastRegistrationForm[user_agreement]"
              type="checkbox"
            >
            <span class="custom-checkbox" />
            <span class="user-modal__agree-text">
              {{ translator.t("main", "I have read the") }}
              <a href="/files/user_agree.pdf" target="_blank">
                {{ translator.t("main", "User Agreement") }}
                <span class="checkbox__required-mark">*</span>
              </a>
            </span>
          </label>
        </div>
        <div class="user-modal__input-wrapper user-modal__input-wrapper_agree">
          <label class="user-modal__agree-label">
            <input
              id="fastregistrationform-agree"
              v-bind="agree"
              v-model="agreeState"
              :checked="values.agree"
              @change="setFieldValue('agree', $event.target.checked)"
              class="custom-checkbox-input"
              name="FastRegistrationForm[agree]"
              type="checkbox"
            >
            <span class="custom-checkbox" />
            <span class="user-modal__agree-text">
              {{ translator.t("main", "I agree") }}
              <a href="/files/personal_agree.pdf" target="_blank">
                {{ translator.t("main", "to the processing of personal data")}}
              <span class="checkbox__required-mark">*</span>
              </a>
            </span>
          </label>
        </div>
        <div class="user-modal__input-wrapper user-modal__input-wrapper_is-subscribed">
          <label class="user-modal__agree-label">
            <input
              id="fastregistrationform-is_subscribed"
              v-bind="isSubscribed"
              :checked="values.isSubscribed"
              @change="setFieldValue(
                'isSubscribed',
                $event.target.checked
              )"
              class="custom-checkbox-input"
              name="FastRegistrationForm[is_subscribed]"
              type="checkbox"
            >
            <span class="custom-checkbox" />
            <span class="user-modal__agree-text">
              {{translator.t('main', 'I hereby confirm')}}
              <a class="checkbox__link checkbox__link_border" href="/files/user_agree.pdf" target="_blank">
                {{ translator.t('user', 'consent to receive marketing and advertising communications') }}
              </a>
            </span>
          </label>
        </div>
        <div v-if="errors.agree || errors.userAgreement" class="user-modal__input-error user-modal__input-error_type-checkbox">
          {{ errors.agree || errors.userAgreement }}
        </div>
        <button
          class="btn btn_secondary modal-body-button"
          :class="{'i-acquirings-button_disabled': !isAccuiringButtonActive}"
          type="submit"
        >
          {{ translator.t("main", "Go to buy") }}
        </button>
      </form>
    </template>
  </i-modal>
</template>

<style lang="scss">

</style>
